import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ASK_CATEGORIES, ASK_PRESETS, fmtMonthDay, type AskAnswerDTO, type AskPreset } from '@wd/shared';
import { TopBar } from '../../app/AppShell';
import { errorText } from '../../lib/api';
import { useLayout } from '../../lib/media';
import { useAsk, useMe, useProjects } from '../../lib/queries';
import { Icon } from '../../ui/icons';
import { MenuList } from '../../ui/menu';
import { AIAvatar, BottomSheet, Button, Modal } from '../../ui/primitives';
import './ask.css';

// 问 AI：用日报回答项目进展、卡点、谁在做什么。只用提问人能看到的日报，回答附上引用的原文。

interface Turn {
  id: number;
  q: string;
  answer?: AskAnswerDTO;
  error?: string;
}

const STORE = 'wd-ask-turns';

function load(): Turn[] {
  try {
    return JSON.parse(sessionStorage.getItem(STORE) ?? '[]') as Turn[];
  } catch {
    return [];
  }
}

export function AskPage() {
  const mobile = useLayout() === 'mobile';
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const run = useAsk();
  const [turns, setTurns] = useState<Turn[]>(load);
  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const asked = useRef(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORE, JSON.stringify(turns.filter((t) => t.answer || t.error).slice(-20)));
    } catch {
      /* 隐私模式下忽略 */
    }
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [turns]);

  const [library, setLibrary] = useState(false);
  const [picking, setPicking] = useState<AskPreset | null>(null);
  const { data: me } = useMe();

  const submit = (question: string, preset?: AskPreset, project?: { id: string; label: string }) => {
    const q = preset ? (project ? `${project.label}：现状、风险和下一步` : preset.question) : question.trim();
    if (q.length < 2 || run.isPending) return;
    const id = Date.now();
    setTurns((t) => [...t, { id, q }]);
    if (!preset) setText('');
    setLibrary(false);
    run.mutate({ question: q, preset: preset?.key ?? null, projectId: project?.id ?? null }, {
      onSuccess: (answer) => setTurns((t) => t.map((x) => (x.id === id ? { ...x, answer } : x))),
      onError: (e) => setTurns((t) => t.map((x) => (x.id === id ? { ...x, error: errorText(e) } : x))),
    });
  };

  // 从 ⌘K 带着问题过来：自动问一次
  useEffect(() => {
    const q = params.get('q');
    if (q && !asked.current) {
      asked.current = true;
      setParams({}, { replace: true });
      submit(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <>
      {!mobile && (
        <TopBar crumbs={['问 AI']} icon="spark">
          {turns.length > 0 && (
            <>
              <Button variant="text" onClick={() => setLibrary(!library)}>
                {library ? '收起常见问题' : '常见问题'}
              </Button>
              <Button variant="text" onClick={() => setTurns([])}>
                清空
              </Button>
            </>
          )}
        </TopBar>
      )}
      <div className={clsx('ask', mobile && 'mobile')}>
        {mobile && (
          <button type="button" className="ask-back" onClick={() => navigate('/me')}>
            <Icon name="chevL" size={18} color="var(--text-3)" />
            <span>我</span>
          </button>
        )}
        <div className="ask-scroll">
          {turns.length === 0 ? (
            <div className="ask-empty">
              <div className="ask-hello">
                <AIAvatar size={24} />
                <span>用日报回答你的问题</span>
              </div>
              <p className="t-meta">直接提问，或者从下面的常见问题开始：每个问题都按项目管理的做法准备好了数据和回答格式。只用你本来就能看到的日报；发给 AI 前，人名和客户名会换成代号。</p>
              <PresetLibrary isManager={!!me?.isManager} onPick={(p) => (p.needsProject ? setPicking(p) : submit('', p))} />
            </div>
          ) : (
            turns.map((t) => <TurnView key={t.id} t={t} />)
          )}
          <div ref={endRef} />
        </div>
        {turns.length > 0 && (library || mobile) && (
          <div className={clsx('ask-library', mobile && !library && 'collapsed')}>
            {mobile && (
              <button type="button" className="ask-toggle" onClick={() => setLibrary(!library)}>
                常见问题
                <Icon name={library ? 'chevD' : 'chevU'} size={12} />
              </button>
            )}
            {library && <PresetLibrary isManager={!!me?.isManager} compact onPick={(p) => (p.needsProject ? setPicking(p) : submit('', p))} />}
          </div>
        )}
        {picking && (
          <ProjectPicker
            mobile={mobile}
            onClose={() => setPicking(null)}
            onPick={(proj) => {
              const p = picking;
              setPicking(null);
              submit('', p, proj);
            }}
          />
        )}
        <form
          className="ask-input"
          onSubmit={(e) => {
            e.preventDefault();
            submit(text);
          }}
        >
          <textarea
            className="wd-textarea"
            rows={mobile ? 2 : 1}
            value={text}
            maxLength={300}
            placeholder="例如：PRJ-07 现在卡在哪？"
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit(text);
              }
            }}
          />
          <Button variant="primary" mobile={mobile} type="submit" disabled={text.trim().length < 2 || run.isPending}>
            {run.isPending ? '思考中…' : '提问'}
          </Button>
        </form>
      </div>
    </>
  );
}

function TurnView({ t }: { t: Turn }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="ask-turn">
      <div className="ask-q">{t.q}</div>
      <div className="ask-a">
        <AIAvatar size={20} />
        <div className="ask-body">
          {t.error ? (
            <div className="c-red">{t.error}</div>
          ) : !t.answer ? (
            <div className="t-meta ask-wait">正在读日报…</div>
          ) : (
            <>
              <Answer text={t.answer.answer} />
              <div className="ask-meta">
                <span className="t-meta">查了{t.answer.scope}</span>
                {t.answer.sources.length > 0 && (
                  <button type="button" className="ask-toggle" onClick={() => setOpen(!open)}>
                    依据 {t.answer.sources.length} 条日报
                    <Icon name={open ? 'chevU' : 'chevD'} size={12} />
                  </button>
                )}
              </div>
              {open && (
                <div className="ask-sources">
                  {t.answer.sources.map((s, i) => (
                    <div key={i} className="ask-src">
                      <span className="t-meta">
                        {fmtMonthDay(s.date)} · <Link to={`/people/${s.memberId}`}>{s.memberName}</Link>
                        {s.projectId && (
                          <>
                            {' · '}
                            <Link to={`/projects/${s.projectId}`}>
                              {s.projectCode} {s.projectName}
                            </Link>
                          </>
                        )}
                        {s.kind === 'note' ? ' · 补充说明' : s.kind === 'plan' ? ' · 明日计划' : s.kind === 'comment' ? ' · 评论' : ''}
                      </span>
                      <span className="c2">{s.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** 回答是纯文本：以“- ”开头的行渲染成列表，其余是段落 */
function Answer({ text }: { text: string }) {
  const blocks: ({ kind: 'p'; text: string } | { kind: 'ul'; items: string[] })[] = [];
  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const m = /^[-•*]\s*(.+)$/.exec(line);
    const last = blocks[blocks.length - 1];
    if (m) {
      if (last?.kind === 'ul') last.items.push(m[1]!);
      else blocks.push({ kind: 'ul', items: [m[1]!] });
    } else blocks.push({ kind: 'p', text: line });
  }
  return (
    <div className="ask-answer">
      {blocks.map((b, i) =>
        b.kind === 'p' ? (
          <p key={i}>{b.text}</p>
        ) : (
          <ul key={i}>
            {b.items.map((it, j) => (
              <li key={j}>{it}</li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}

/** 常见问题库：按项目管理的维度分组；每个问题背后有一条服务端规则（数据范围 + 回答格式） */
function PresetLibrary({ isManager, compact, onPick }: { isManager: boolean; compact?: boolean; onPick: (p: AskPreset) => void }) {
  return (
    <div className={clsx('ask-groups', compact && 'compact')}>
      {ASK_CATEGORIES.map((cat) => {
        const list = ASK_PRESETS.filter((p) => p.category === cat && (!p.managerOnly || isManager));
        if (!list.length) return null;
        return (
          <div key={cat} className="ask-group">
            <div className="ask-group-name">{cat}</div>
            <div className="ask-sugs">
              {list.map((p) => (
                <button key={p.key} type="button" className="ask-sug" onClick={() => onPick(p)}>
                  {p.question}
                  {p.needsProject && <Icon name="chevR" size={12} />}
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 「某个项目的现状、风险和下一步」：先选项目（只列看得到的） */
function ProjectPicker({ mobile, onClose, onPick }: { mobile: boolean; onClose: () => void; onPick: (p: { id: string; label: string }) => void }) {
  const { data: projects = [] } = useProjects();
  const list = projects.filter((p) => p.visible && p.status !== 'cancelled');
  const menu = (
    <MenuList
      search
      items={list.map((p) => ({ value: p.id, label: p.name, hint: `${p.code} · ${p.ownerName}` }))}
      selected={null}
      onPick={(id) => {
        const p = list.find((x) => x.id === id);
        if (p) onPick({ id: p.id, label: `${p.code} ${p.name}` });
      }}
      emptyText="没有你能看到的项目"
    />
  );
  return mobile ? (
    <BottomSheet onClose={onClose}>
      <div style={{ padding: '4px 12px 12px' }}>
        <div className="t-block-title" style={{ padding: '0 8px 8px' }}>
          选一个项目
        </div>
        {menu}
      </div>
    </BottomSheet>
  ) : (
    <Modal width={440} title="选一个项目" subtitle="AI 会整理它的现状、风险和下一步" onClose={onClose}>
      {menu}
    </Modal>
  );
}
